import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  CancellationFlowStatus,
  getCancellationFlowStatusClass,
  getCancellationFlowStatusLabel,
  isCancellationFlowDeletable,
  isCancellationFlowOver,
} from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { CustomerAccountType } from 'src/app/_enums/customer-account-type.enum';
import {
  getInsuranceTypeIcon,
  getInsuranceTypeLabel,
} from 'src/app/_enums/insurance-type.enum';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { User } from 'src/app/_types/auth.types';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { TaskDetailsComponent } from 'src/app/Dashboard/proposals/task-details/task-details.component';
import { CancellationCancelModalComponent } from 'src/app/shared/components/cancellation-cancel-modal/cancellation-cancel-modal.component';
import { CancellationLetterCardComponent } from 'src/app/shared/components/cancellation-cards/cancellation-letter-card.component';
import { CancellationLinkCardComponent } from 'src/app/shared/components/cancellation-cards/cancellation-link-card.component';
import { CancellationDeleteModalComponent } from 'src/app/shared/components/cancellation-delete-modal/cancellation-delete-modal.component';
import { CancellationDocumentModalComponent } from 'src/app/shared/components/cancellation-document-modal/cancellation-document-modal.component';
import { CancellationEditModalComponent } from 'src/app/shared/components/cancellation-edit-modal/cancellation-edit-modal.component';
import { CancellationFinalizeModalComponent } from 'src/app/shared/components/cancellation-finalize-modal/cancellation-finalize-modal.component';
import { CancellationLetterModalComponent } from 'src/app/shared/components/cancellation-letter-modal/cancellation-letter-modal.component';
import { CancellationSendModalComponent } from 'src/app/shared/components/cancellation-send-modal/cancellation-send-modal.component';
import { CustomerProfilePanelComponent } from 'src/app/shared/components/customer-profile-panel/customer-profile-panel.component';
import {
  DetailsHeaderBadge,
  DetailsHeaderComponent,
} from 'src/app/shared/components/details-header/details-header.component';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { CancellationDetailsService } from './cancellation-details.service';
import { CancellationDocumentsComponent } from './cancellation-documents.component';
import { CancellationLogComponent } from './cancellation-log.component';
import { CancellationRequestInfoComponent } from './cancellation-request-info.component';

/**
 * A cancellation, in the same slide-in as an insurance flow, minus the
 * stepper: the notice cards, then the customer, the request, and the log.
 *
 * Opens two ways. A host with a list behind it binds `cancellationId` to the
 * URL, so opening and closing is a navigation that survives a reload and the
 * back button; any other host calls `openModal(id)`. Both land in `sync`,
 * which waits for the slide-in when an id arrives before the view does — a
 * cold deep link.
 */
@Component({
  selector: 'app-cancellation-details-modal',
  standalone: true,
  imports: [
    SlideInModalComponent,
    SpinnerComponent,
    DetailsHeaderComponent,
    CustomerProfilePanelComponent,
    CancellationLinkCardComponent,
    CancellationLetterCardComponent,
    CancellationLetterModalComponent,
    CancellationRequestInfoComponent,
    CancellationDocumentsComponent,
    CancellationLogComponent,
    TaskDetailsComponent,
    CancellationSendModalComponent,
    CancellationFinalizeModalComponent,
    CancellationEditModalComponent,
    CancellationDeleteModalComponent,
    CancellationCancelModalComponent,
    CancellationDocumentModalComponent,
    IconsModule,
  ],
  templateUrl: './cancellation-details-modal.component.html',
  styleUrl: './cancellation-details-modal.component.css',
  // One mediator per modal, shared with its sections and nobody else.
  providers: [CancellationDetailsService],
})
export class CancellationDetailsModalComponent implements OnInit {
  private readonly detailsService = inject(CancellationDetailsService);

  private readonly cancellationService = inject(CancellationService);

  /** The cancellation to show; null closes the modal. */
  @Input()
  set cancellationId(id: string | null) {
    this.requestedId = id;
    this.sync();
  }

  /** The modal closed itself, so a URL-bound host can drop the id. */
  @Output() readonly closed = new EventEmitter<void>();

  /** A link or a letter landed: the refreshed aggregate, for a list behind to repaint. */
  @Output() readonly changed =
    new EventEmitter<CancellationFlowDetailsResponse>();

  /** "Voir l'affaire" on a card: a flow id the host knows how to open. */
  @Output() readonly viewFlow = new EventEmitter<string>();

  /** The cancellation was removed: the sheet closes and the host refetches. */
  @Output() readonly deleted = new EventEmitter<void>();

  // Static: the slide-in is the template's root, and the setter above may
  // need it before this component's view has been checked.
  @ViewChild('modal', { static: true })
  private readonly modal?: SlideInModalComponent;

  protected readonly Status = CancellationFlowStatus;

  private requestedId: string | null = null;

  /**
   * The id whose load is in flight or on screen. The setter and ngOnInit both
   * reach `sync` on a first binding, and the static query is resolved by then:
   * without this the deep link would fetch the cancellation twice.
   */
  private openedId: string | null = null;

  @ViewChild('taskDetailsModal')
  private readonly taskDetailsModal?: TaskDetailsComponent;

  ngOnInit(): void {
    // Replays whatever the input already holds, now that `modal` is resolved.
    this.sync();
  }

  openModal(id: string): void {
    this.requestedId = id;
    this.sync();
  }

  private sync(): void {
    if (!this.modal) return;
    if (!this.requestedId) {
      this.modal.closeModal(false);
      this.reset();
      return;
    }
    if (this.openedId !== this.requestedId) {
      this.openedId = this.requestedId;
      this.detailsService.load(this.requestedId);
    }
    this.modal.openModal();
  }

  private reset(): void {
    this.openedId = null;
    this.detailsService.clear();
  }

  protected onClosed(): void {
    this.reset();
    this.closed.emit();
  }

  protected onChanged(details: CancellationFlowDetailsResponse): void {
    this.detailsService.replaceDetails(details);
    this.changed.emit(details);
  }

  /** Nothing left to show: the sheet closes, and the host drops it from its list. */
  protected onDeleted(): void {
    this.deleted.emit();
    this.modal?.closeModal(false);
  }

  /**
   * A request that is over stays as it ended; any other can be called off —
   * including the early ones a deletion would erase instead.
   */
  protected get callable(): boolean {
    const { details } = this;
    return !!details && !isCancellationFlowOver(details.status);
  }

  /** Only a cancellation nobody has signed can be taken back. */
  protected get deletable(): boolean {
    const { details } = this;
    return !!details && isCancellationFlowDeletable(details.status);
  }

  protected get details(): CancellationFlowDetailsResponse | null {
    return this.detailsService.state.details;
  }

  protected get userProfile(): User | null {
    return this.detailsService.state.userProfile;
  }

  protected get isLoading(): boolean {
    return this.detailsService.state.isLoading;
  }

  protected get notFound(): boolean {
    return this.detailsService.state.notFound;
  }

  protected get failed(): boolean {
    return this.detailsService.state.failed;
  }

  /** The branch, what this record is, and the insurer — text only, it has no logo. */
  protected get headerBadges(): DetailsHeaderBadge[] {
    const { details } = this;
    if (!details) return [];
    // A request the customer filed may name neither the branch nor the insurer
    // until an operator completes it; a badge for nothing reads worse than none.
    return [
      ...(details.insuranceType
        ? [
            {
              label: getInsuranceTypeLabel(details.insuranceType),
              iconUrl: getInsuranceTypeIcon(details.insuranceType) || null,
            },
          ]
        : []),
      { label: $localize`Demande de résiliation`, modifier: 'pink' },
      ...(details.insurer.name ? [{ label: details.insurer.name }] : []),
      {
        label: getCancellationFlowStatusLabel(details.status),
        modifier: getCancellationFlowStatusClass(details.status),
      },
    ];
  }

  /** The insured member for a person, the company otherwise — as the flow modal does. */
  protected get headerCustomerName(): string | null {
    const user = this.userProfile;
    if (user?.type === CustomerAccountType.COMPANY) {
      return (
        user.companyDetails?.companyName ?? `${user.firstName} ${user.lastName}`
      );
    }
    return this.details?.memberName ?? this.details?.userName ?? null;
  }

  /**
   * "Voir l'affaire" from inside the cancellation: the flow's modal opens on
   * top, the cancellation staying open beneath it to come back to.
   */
  protected openFlow(flowId: string): void {
    this.taskDetailsModal?.openModal(flowId);
  }

  /** The affaire on top changed: the cancellation is re-read, its cards and the list behind with it. */
  protected onFlowUpdated(): void {
    const id = this.openedId;
    if (!id) return;
    this.cancellationService.getCancellationFlow(id).subscribe({
      next: (res) => res.data && this.onChanged(res.data),
    });
  }
}
